import { Children, useEffect, useState } from 'react';
import { withStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';
import { ReactComponent as ArrowLeftIcon } from 'assets/images/icon_arrow_left_no_tail.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/images/icon_arrow_right_no_tail.svg';
import noop from 'lodash/fp/noop';
import ArrowLeftIconB from 'assets/images/left-arrow.svg';
import ArrowRightIconB from 'assets/images/right-arrow.svg';
import SliderControls from './SliderControls';

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const styles = theme => ({
  root: ({ width, height, nextHighlight }) => ({
    width: nextHighlight > 0 ? width + nextHighlight * 4 : width,
    height,
    background: 'transparent',
    flexGrow: 1,
    position: 'relative',
    ...(nextHighlight > 0 ? { padding: `0 ${nextHighlight}px` } : {}),
  }),
  controls: {
    width: '100%',
    padding: `${theme.spacing.s16}px 0`,
  },
  floatingControls: {
    width: '100%',
    position: 'absolute',
    bottom: 20,
    zIndex: 2,
  },
  whiteControls: {
    position: 'absolute',
    bottom: 0,
    margin: 'auto',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    '& button': {
      opacity: 0.5,
      background: 'white',
      '&$arrowButton': {
        background: 'transparent',
      },
      '& svg': {
        fill: 'white',
        '& path': {
          stroke: 'white',
        },
      },
    },
  },
  arrowButton: {
    appearance: 'none',
    height: theme.spacing.s16,
    backgroundColor: 'transparent',
    border: 'none',
    opacity: 0.5,
    '&:first-child': {
      margin: '0 auto 0 0',
    },
    '&:last-child': {
      margin: '0 0 0 auto',
    },
    '&:enabled': {
      cursor: 'pointer',
      opacity: 1,
    },
  },
  next: {
    position: 'absolute',
    top: '30%',
    /* XXX: better than px but still brittle. */
    right: '-25%',
    appearance: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  prev: {
    cursor: 'pointer',
    position: 'absolute',
    top: '30%',
    /* XXX: better than px but still brittle. */
    left: '-25%',
    appearance: 'none',
    background: 'none',
    border: 'none',
  },
});

const getCurrentIndex = (currentIndex, maxIndex) =>
  ((currentIndex % maxIndex) + maxIndex) % maxIndex;

const slideRendererHOC = (childrenArray, childrenCount) => {
  const SlideRenderer = ({ index, key }) => (
    <div key={key}>{childrenArray[getCurrentIndex(index, childrenCount)]}</div>
  );
  SlideRenderer.propTypes = {
    index: PropTypes.number.isRequired,
    key: PropTypes.number.isRequired,
  };
  return SlideRenderer;
};

const Carousel = ({
  arrows,
  bigArrows,
  children,
  classes,
  controlsPosition,
  controlsTheme,
  dataFrom,
  floatingControls,
  heap,
  isInfinite,
  isLooping,
  step,
  whiteControls,
  nextHighlight,
}) => {
  const [activeStep, setActiveStep] = useState(step);

  const maxSteps = Children.count(children);
  const childrenArray = Children.toArray(children);
  const getClassForControlsFlag = () => {
    if (whiteControls) {
      return classes.whiteControls;
    }
    if (floatingControls) {
      return classes.floatingControls;
    }
    return classes.controls;
  };

  useEffect(() => {
    const currentIndex = getCurrentIndex(activeStep, maxSteps) + 1;
    if (window.heap && window.heap.track && heap) {
      window.heap.track(`${heap} slide ${currentIndex}`);
    }
  }, [activeStep, heap]);

  return (
    <div className={classes.root}>
      {isInfinite ? (
        <VirtualizeSwipeableViews
          enableMouseEvents
          index={activeStep}
          onChangeIndex={setActiveStep}
          slideRenderer={slideRendererHOC(childrenArray, maxSteps)}
          slideStyle={{ width: '100%' }}
          style={nextHighlight > 0 ? { padding: `0 ${nextHighlight}px` } : null}
        />
      ) : (
        <SwipeableViews
          enableMouseEvents
          index={activeStep}
          onChangeIndex={setActiveStep}
          style={nextHighlight > 0 ? { padding: `0 ${nextHighlight}px` } : null}
        >
          {Children.map(children, (child, index) => (
            <div>{Math.abs(activeStep - index) <= 3 && child}</div>
          ))}
        </SwipeableViews>
      )}
      {maxSteps > 1 && (
        <SliderControls
          arrowLeft={
            arrows && (
              <button
                className={classes.arrowButton}
                disabled={activeStep <= 0 && !isLooping && !isInfinite}
                onClick={() =>
                  isInfinite
                    ? setActiveStep(activeStep - 1)
                    : setActiveStep((activeStep || maxSteps) - 1)
                }
                type="button"
              >
                <ArrowLeftIcon title="Left Arrow" />
              </button>
            )
          }
          arrowRight={
            arrows && (
              <button
                className={classes.arrowButton}
                disabled={activeStep >= maxSteps - 1 && !isLooping && !isInfinite}
                onClick={() =>
                  isInfinite
                    ? setActiveStep(activeStep + 1)
                    : setActiveStep((activeStep + 1) % maxSteps)
                }
                type="button"
              >
                <ArrowRightIcon title="Right Arrow" />
              </button>
            )
          }
          classes={{
            root: `${getClassForControlsFlag()}`,
          }}
          currentIndex={getCurrentIndex(activeStep, maxSteps)}
          dataFrom={dataFrom}
          onClick={isInfinite ? noop : setActiveStep}
          position={controlsPosition}
          size={maxSteps}
          theme={controlsTheme}
        />
      )}
      {bigArrows && (
        <>
          <button
            className={classes.prev}
            onClick={() => setActiveStep((activeStep || maxSteps) - 1)}
            type="button"
          >
            <img
              alt="Carousel previous slide - vector"
              data-click="change-slide-arrow"
              data-from="hero"
              src={ArrowLeftIconB}
              title="Left Arrow"
            />
          </button>
          <button
            className={classes.next}
            onClick={() => setActiveStep((activeStep + 1) % maxSteps)}
            type="button"
          >
            <img
              alt="Carousel next slide - vector"
              data-click="change-slide-arrow"
              data-from="hero"
              src={ArrowRightIconB}
              title="Right Arrow"
            />
          </button>
        </>
      )}
    </div>
  );
};

Carousel.propTypes = {
  arrows: PropTypes.bool,
  bigArrows: PropTypes.bool,
  children: PropTypes.node.isRequired,
  dataFrom: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  nextHighlight: PropTypes.number,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  isInfinite: PropTypes.bool,
  controlsPosition: PropTypes.string,
  controlsTheme: PropTypes.string,
  whiteControls: PropTypes.bool,
  floatingControls: PropTypes.bool,
  heap: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isLooping: PropTypes.bool,
  step: PropTypes.number,
};

Carousel.defaultProps = {
  arrows: true,
  bigArrows: false,
  controlsPosition: null,
  controlsTheme: 'noir',
  dataFrom: 'Carousel',
  floatingControls: false,
  heap: false,
  isInfinite: false,
  isLooping: false,
  step: 0,
  nextHighlight: 0,
  width: 'auto',
  whiteControls: false,
};

export default withStyles(styles)(Carousel);
